import {
  Icon,
  Popover,
  PopoverBodyProps,
  PopoverContentProps,
  PopoverRootProps,
  Portal,
} from '@chakra-ui/react';
import React, { FC } from 'react';
import { MdClose } from 'react-icons/md';

/*
 * Built on Chakra Popover (namespace-only in v3, same restructuring as
 * Dialog/Menu). This wrapper keeps v2's flat prop shape as its external API
 * — absorbing the Root/Trigger/Positioner/Content restructuring here means
 * none of its ~13 consumers need to change.
 *
 * v2 prop → v3 translation, none of which is exposed to callers:
 * - `isOpen`/`onOpen`/`onClose` → `open`/`onOpenChange` (Popover.Root's
 *   `onOpenChange` fires on both open and close; `onOpen` only exists in v2)
 * - `placement` is unchanged in name/values but now lives under
 *   `positioning.placement` rather than being a direct prop — merged with
 *   any `positioning` a caller passes via `popoverProps` (same
 *   caller-clobber bug fixed on `MDropdownActions` in Track D: a later
 *   literal `positioning={{placement}}` would otherwise fully replace
 *   whatever `popoverProps.positioning` held, since JSX prop spreading isn't
 *   a deep merge).
 * - `usePortal` (boolean, default false) is unchanged in meaning — v2 needed
 *   an explicit `<Portal>` wrap for this, and so does v3 (`portalled` is a
 *   real Popover.Root prop, but confirmed via source it only adjusts the
 *   machine's internal tab-order assumptions, it doesn't itself render a
 *   portal — same non-obvious gap Track E's `MCenterModal` already
 *   documents for Dialog).
 * - v2's `trigger: 'click' | 'hover'` is gone: v3's Popover has no hover
 *   trigger (`@zag-js/popover` has no such prop) and no caller ever passed
 *   `'hover'`. Removing it makes a future caller fail tsc rather than get a
 *   click popover. A hover card is `HoverCard` in v3.
 * - `Popover.CloseTrigger` renders no default icon in v3 (v2's
 *   `PopoverCloseButton` did) — same fix as `MCenterModal`/`Dialog`. zag
 *   also names it "close" where v2's was "Close", so the label is set.
 */

type PopoverPlacement = NonNullable<
  PopoverRootProps['positioning']
>['placement'];

interface MPopoverWrapperProps {
  isOpen: boolean;
  showArrow?: boolean;
  placement: PopoverPlacement;
  onOpen?: () => void;
  onClose: () => void;
  usePortal?: boolean;
  showCloseBtn?: boolean;
  triggerContent: React.ReactNode;
  headerContent?: React.ReactNode;
  bodyContent: React.ReactNode;
  footerContent?: React.ReactNode;
  popoverProps?: Omit<PopoverRootProps, 'children' | 'open' | 'onOpenChange'>;
  popoverContentProps?: PopoverContentProps;
  popoverBodyProps?: PopoverBodyProps;
}

const MPopoverWrapper: FC<MPopoverWrapperProps> = ({
  isOpen,
  onOpen,
  onClose,
  placement,
  showArrow,
  usePortal,
  showCloseBtn,
  triggerContent,
  headerContent,
  bodyContent,
  footerContent,
  popoverProps,
  popoverContentProps,
  popoverBodyProps,
}: MPopoverWrapperProps) => {
  const popoverContent = (
    <Popover.Positioner>
      <Popover.Content {...popoverContentProps}>
        {showArrow && (
          <Popover.Arrow>
            <Popover.ArrowTip />
          </Popover.Arrow>
        )}
        {showCloseBtn && (
          <Popover.CloseTrigger aria-label="Close">
            <Icon asChild>
              <MdClose />
            </Icon>
          </Popover.CloseTrigger>
        )}
        {headerContent && <Popover.Header>{headerContent}</Popover.Header>}
        <Popover.Body {...popoverBodyProps}>{bodyContent}</Popover.Body>
        {footerContent && <Popover.Footer>{footerContent}</Popover.Footer>}
      </Popover.Content>
    </Popover.Positioner>
  );

  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      restoreFocus={false}
      {...popoverProps}
      positioning={{ placement, ...popoverProps?.positioning }}
      onOpenChange={(details) => {
        if (details.open) {
          onOpen?.();
        } else {
          onClose();
        }
      }}
    >
      <Popover.Trigger asChild>{triggerContent}</Popover.Trigger>
      {isOpen &&
        (usePortal ? <Portal>{popoverContent}</Portal> : popoverContent)}
    </Popover.Root>
  );
};

export default MPopoverWrapper;
